import { Target, Award, ShieldCheck, Zap } from 'lucide-react';

const ARCHERY_IMAGE = "https://images.unsplash.com/photo-1709568859007-7a2960ac8629?w=800&q=80";

const archeryFeatures = [
  {
    icon: Target,
    title: 'Compound & Recurve Bows',
    desc: 'Top brands like Hoyt, Mathews, Bear, and PSE. Bows for beginners through competition shooters.',
  },
  {
    icon: Zap,
    title: 'Crossbows & Accessories',
    desc: 'Crossbows, arrows, broadheads, quivers, sights, stabilizers, and release aids in stock.',
  },
  {
    icon: Award,
    title: 'Buy, Sell & Trade',
    desc: 'Bring in your used archery gear for cash or trade up to something new. Fair appraisals every time.',
  },
  {
    icon: ShieldCheck,
    title: 'Expert Advice',
    desc: 'Our team can help you find the right setup for hunting season or target shooting.',
  },
];

export default function ArcherySection() {
  return (
    <section
      id="gun-range"
      data-testid="archery-section"
      className="relative py-20 md:py-28 bg-background noise-overlay"
      aria-label="Large selection of archery equipment at Cave City Pawn"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Image */}
          <div className="relative">
            <div className="relative rounded-rock-lg overflow-hidden border-4 border-dirt-brown/30 shadow-deep group">
              <img
                src={ARCHERY_IMAGE}
                alt="Large selection of archery bows and equipment at Cave City Pawn Shop"
                className="w-full h-64 md:h-96 object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                loading="lazy"
              />
            </div>
            {/* Decorative badge */}
            <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 w-20 h-20 md:w-28 md:h-28 bg-magma-red rounded-full flex flex-col items-center justify-center shadow-deep rotate-12">
              <span className="text-bone-white font-heading text-xs md:text-sm uppercase leading-tight text-center">
                Archery<br/>Gear
              </span>
            </div>
          </div>

          {/* Content */}
          <div>
            <p className="text-sm font-body font-semibold text-magma-red uppercase tracking-widest mb-3">Bows, Arrows & More</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading text-dirt-brown uppercase mb-5 chisel-heading">
              Archery <span className="text-magma-red">Equipment</span>
            </h2>
            <p className="text-base md:text-lg text-foreground font-body leading-relaxed mb-6">
              Cave City Pawn carries one of the largest selections of archery equipment in the area! 
              Whether you are gearing up for hunting season, getting into competitive target shooting, 
              or just picking up the sport for the first time, we have the bows, crossbows, arrows, 
              and accessories you need at prices that cannot be beat.
            </p>

            <div className="space-y-4 mb-8">
              {archeryFeatures.map((feat) => {
                const Icon = feat.icon;
                return (
                  <div key={feat.title} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-dirt-brown rounded-full flex items-center justify-center shrink-0 shadow-soft">
                      <Icon size={20} className="text-accent" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-dirt-brown uppercase">{feat.title}</h3>
                      <p className="text-sm text-muted-foreground font-body">{feat.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Highlight callout */}
            <div
              data-testid="archery-callout"
              className="bg-secondary/10 border-2 border-secondary/30 rounded-rock-lg p-4 flex items-start gap-3"
            >
              <Target size={20} className="text-secondary shrink-0 mt-0.5" />
              <p className="text-sm text-foreground font-body">
                <strong>Looking for something specific?</strong> If we do not have it in stock, 
                we can help you find it. We also buy and trade used archery equipment — 
                bring yours in for a free appraisal!
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
